import { type UUID } from 'node:crypto';
// Import base Database type and helpers
import { type Database, type Tables, type TablesInsert, type TablesUpdate } from '@/types/supabase'; 

// Define specific types using helpers
type Poet = Tables<'poets'>;
type NewPoet = TablesInsert<'poets'>; 
type UpdatePoet = TablesUpdate<'poets'>; 
// Use Tables helper for the poet_details view
type PoetDetails = Tables<'poet_details'>; 
type PoetWithPoems = Poet & { poems: any[] }; // Define PoetWithPoems locally

import { AppError, ErrorType, handleError, logError } from '@/utils/error/errorHandler'; 
import { cacheManager } from '@/utils/cache/cacheManager'; 
import { CACHE_TTL, CacheEntityType, CACHE_KEY_PATTERNS } from '@/utils/cache/cacheConfig'; 
import { validate as isUUID } from 'uuid'; 
import { type PaginatedResult, type PaginationParams, createPaginatedResult } from '@/utils/pagination/paginationUtils'; 
import { invalidateRelatedCaches, invalidateEntityCache, invalidateEntityById } from '@/utils/cache/cacheInvalidation'; 
import { validateObject } from '@/utils/validation'; 
import { validate } from '@/utils/validationWrapper'; 
import { poetSchema } from '@/validation/schemas'; 
import { executeServiceOperation, getWithCache, ServiceResult } from '@/utils/service/serviceUtils';
import { validateUUID } from '@/utils/validationWrapper';
import { OpenAI } from 'openai'; // Assuming OpenAI SDK is installed
import { fetchWithAuth } from '@/lib/apiUtils'; // <-- Import authenticated fetch

// Predefined technical points for well-known poets (copy from our update script)
const KNOWN_POET_STYLES = {
  'Emily Dickinson': [
    'Signature em dashes and unconventional capitalization creating disruptive rhythm',
    'Compact hymnal quatrains with subversive off-rhymes and metrical variations',
    'Meditation on mortality and eternity',
    'Revelatory moments in natural settings'
  ],
  'Walt Whitman': [
    'Revolutionary free verse with expansive cataloging lines that stretch across pages',
    'Democratic anaphora and parallelism creating an incantatory, biblical cadence',
    'Celebration of American democracy',
    'Embodiment of cosmic consciousness'
  ],
  'Robert Frost': [
    'Deceptively simple iambic blank verse masking philosophical complexity',
    'New England vernacular blended with traditional metrical forms',
    'New England rural settings and characters',
    'Ambiguous philosophical conclusions'
  ],
  // Add more poets as needed
};

// Add type for style points
type StylePoints = [string, string, string, string];

// Interface for the AI service response
interface StylePointResponse {
  style_points: StylePoints;
}

/**
 * Invalidates all poet-related cache entries
 * @deprecated Use invalidateEntityCache(CacheEntityType.POET) instead
 */
export function invalidatePoetCache(): void {
  invalidateEntityCache(CacheEntityType.POET);
}

/**
 * Invalidates cache entries for a specific poet
 * @param poetId - The ID of the poet
 * @deprecated Use invalidateEntityById(CacheEntityType.POET, poetId) instead
 */
export function invalidatePoetCacheById(poetId: string): void {
  invalidateEntityById(CacheEntityType.POET, poetId);
}

/**
 * Retrieves all poets from the API, ordered by name (assumed default API order)
 * @returns Array of poet objects
 * @throws AppError if the operation fails
 */
export async function getAllPoets(): Promise<Poet[]> {
  console.log("poetService: getAllPoets called");
  
  const cacheKey = CACHE_KEY_PATTERNS.ALL(CacheEntityType.POET);
  const cachedPoets = cacheManager.get<Poet[]>(cacheKey);
  if (cachedPoets) {
    console.log("poetService: getAllPoets returning cached data", cachedPoets.length);
    return cachedPoets;
  }
  
  try {
    console.log("poetService: getAllPoets fetching from API");
    const apiUrl = '/api/poets'; 
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: getAllPoets API error", { status: response.status, statusText: response.statusText, errorBody });
      throw new AppError({
        message: `API Error fetching poets: ${response.statusText} (Status: ${response.status})`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody }
      });
    }

    const poets: Poet[] = await response.json();
    console.log("poetService: getAllPoets fetched from API", poets.length);
    
    cacheManager.set(cacheKey, poets, CACHE_TTL.MEDIUM);
    
    return poets;

  } catch (error) {
    console.error("poetService: getAllPoets error", error);
    const appError = handleError(error, 'getAllPoets');
    logError(appError, 'getAllPoets');
    
    if (appError.type === ErrorType.NOT_FOUND || appError.statusCode === 404) {
      console.log("poetService: getAllPoets returning empty array for NOT_FOUND");
      return [];
    }
    
    throw appError;
  }
}

/**
 * Retrieves all poets with their associated poems from the API
 * @returns Array of poet objects with poems
 * @throws AppError if the operation fails
 */
export async function getAllPoetsWithPoems(): Promise<PoetWithPoems[]> {
  const cacheKey = `${CacheEntityType.POET}s:with-poems`;
  const cachedPoets = cacheManager.get<PoetWithPoems[]>(cacheKey);
  if (cachedPoets) {
    console.log("poetService: getAllPoetsWithPoems returning cached data");
    return cachedPoets;
  }
  
  try {
    console.log("poetService: getAllPoetsWithPoems fetching from API");
    const apiUrl = '/api/poets?includePoems=true'; 
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: getAllPoetsWithPoems API error", { status: response.status, statusText: response.statusText, errorBody });
      throw new AppError({
        message: `API Error fetching poets with poems: ${response.statusText} (Status: ${response.status})`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody }
      });
    }

    const poets: PoetWithPoems[] = await response.json();
    console.log("poetService: getAllPoetsWithPoems fetched from API", poets.length);
    
    cacheManager.set(cacheKey, poets, CACHE_TTL.MEDIUM);
    
    return poets;

  } catch (error) {
    console.error("poetService: getAllPoetsWithPoems error", error);
    const appError = handleError(error, 'getAllPoetsWithPoems');
    logError(appError, 'getAllPoetsWithPoems');
    
    if (appError.type === ErrorType.NOT_FOUND || appError.statusCode === 404) {
      console.log("poetService: getAllPoetsWithPoems returning empty array for NOT_FOUND");
      return [];
    }
    
    throw appError;
  }
}

/**
 * Retrieves a poet by their unique ID from the API
 * @param id - The poet's UUID
 * @returns A ServiceResult containing the poet or error information
 * @throws AppError if validation fails
 */
export async function getPoetById(id: string): Promise<ServiceResult<Poet>> {
  // Validate ID format first - Use the dedicated validateUUID function
  validateUUID(id, 'poetId', { poetId: id });
  
  const cacheKey = CACHE_KEY_PATTERNS.BY_ID(CacheEntityType.POET, id);
  
  return executeServiceOperation(async () => {
    return getWithCache<Poet>(
      cacheKey,
      async () => {
        console.log(`poetService: getPoetById fetching from API for ID: ${id}`);
        const apiUrl = `/api/poets/${id}`;
        const response = await fetchWithAuth(apiUrl);

        if (!response.ok) {
          let errorBody: any = null;
          try { errorBody = await response.json(); } catch (e) { /* ignore */ }
          console.error("poetService: getPoetById API error", { status: response.status, statusText: response.statusText, errorBody, url: apiUrl });

          const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;
          
          throw new AppError({
            message: `API Error fetching poet ${id}: ${response.statusText} (Status: ${response.status})`,
            type: errorType,
            statusCode: response.status,
            metadata: { url: apiUrl, responseBody: errorBody, poetId: id }
          });
        }

        const data: Poet = await response.json();
        console.log(`poetService: getPoetById fetched from API successfully for ID: ${id}`);

        if (!data) {
            console.error(`poetService: getPoetById received null data from API despite OK status for ID: ${id}`);
            throw new AppError({
                message: `API returned null data for Poet ID ${id}`,
                type: ErrorType.UNEXPECTED,
                statusCode: 500, 
                metadata: { url: apiUrl, poetId: id }
            });
        }
        
        if (typeof data !== 'object' || !data.id || data.id !== id) {
             console.error("poetService: getPoetById received unexpected data format from API", { data, expectedId: id });
             throw new AppError({
                message: `API returned unexpected data format for poet ID ${id}`,
                type: ErrorType.UNEXPECTED,
                statusCode: 500, 
                metadata: { url: apiUrl, responseBody: data, poetId: id }
             });
        }
        
        return data; 
      },
      CACHE_TTL.MEDIUM 
    );
  }, `getPoetById(${id})`);
}

/**
 * Creates a new poet via the API
 * @param poet - The poet data to insert (using NewPoet type)
 * @returns The created poet object
 * @throws AppError if the operation fails or validation fails
 */
export async function createPoet(poet: NewPoet): Promise<Poet> {
  const validationResults = validateObject(poet, poetSchema, 'poet');
  validate(validationResults, { poet });
  
  try {
    console.log("poetService: createPoet calling ADMIN API");
    const apiUrl = '/api/admin/add-poet';
    const response = await fetchWithAuth(apiUrl, {
      method: 'POST',
      body: JSON.stringify(poet),
    });

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: createPoet API error", { status: response.status, statusText: response.statusText, errorBody, requestBody: poet });
      throw new AppError({
        message: `API Error creating poet: ${response.statusText} (Status: ${response.status})`,
        type: ErrorType.NETWORK, 
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, requestBody: poet }
      });
    }

    const createdPoet: Poet = await response.json();
    console.log(`poetService: createPoet successful via API, created poet ID: ${createdPoet.id}`);
    
    if (!createdPoet || typeof createdPoet !== 'object' || !createdPoet.id) {
       console.error('poetService: createPoet received unexpected data format from API', { responseData: createdPoet });
       throw new AppError({
          message: 'API returned unexpected data format after creating poet',
          type: ErrorType.UNEXPECTED,
          statusCode: 500,
          metadata: { url: apiUrl, responseBody: createdPoet }
       });
    }
    
    invalidateRelatedCaches(CacheEntityType.POET, createdPoet.id, createdPoet); 
    
    return createdPoet;

  } catch (error) {
    const appError = handleError(error, 'createPoet', { poet });
    logError(appError, 'createPoet');
    throw appError;
  }
}

/**
 * Updates an existing poet via the API
 * @param id - The poet's UUID
 * @param updates - The poet data to update (using UpdatePoet type)
 * @returns The updated poet object
 * @throws AppError if the poet is not found, validation fails, or the operation fails
 */
export async function updatePoet(id: string, updates: UpdatePoet): Promise<Poet> {
  validate([{ validator: isUUID, value: id, message: 'Invalid Poet ID format' }], { poetId: id });
  const validationResults = validateObject(updates, poetSchema, 'poet');
  validate(validationResults, { poetId: id, updates });
  
  try {
    console.log(`poetService: updatePoet calling API for ID: ${id}`);
    const apiUrl = `/api/poets/${id}`;
    const response = await fetchWithAuth(apiUrl, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: updatePoet API error", { status: response.status, statusText: response.statusText, errorBody, requestBody: updates });

      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;
      
      throw new AppError({
        message: `API Error updating poet ${id}: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, requestBody: updates, poetId: id }
      });
    }

    const updatedPoet: Poet = await response.json();
    console.log(`poetService: updatePoet successful via API for ID: ${id}`);

    if (!updatedPoet || typeof updatedPoet !== 'object' || updatedPoet.id !== id) {
       console.error('poetService: updatePoet received unexpected data format from API', { responseData: updatedPoet, expectedId: id });
       throw new AppError({
          message: `API returned unexpected data format after updating poet ${id}`,
          type: ErrorType.UNEXPECTED,
          statusCode: 500,
          metadata: { url: apiUrl, responseBody: updatedPoet, poetId: id }
       });
    }
    
    invalidateRelatedCaches(CacheEntityType.POET, id, updatedPoet); 
    
    return updatedPoet;

  } catch (error) {
    const appError = handleError(error, 'updatePoet', { poetId: id, updates });
    logError(appError, 'updatePoet');
    throw appError;
  }
}

/**
 * Deletes a poet via the API
 * @param id - The poet's UUID
 * @returns The deleted poet object (as returned by the API)
 * @throws AppError if validation fails or the operation fails
 */
export async function deletePoet(id: string): Promise<Poet> {
  validate([{ validator: isUUID, value: id, message: 'Invalid Poet ID format' }], { poetId: id });
  
  try {
    console.log(`poetService: deletePoet calling ADMIN API for ID: ${id}`);
    const apiUrl = `/api/admin/poets/${id}`;
    const response = await fetchWithAuth(apiUrl, {
      method: 'DELETE',
    });

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: deletePoet API error", { status: response.status, statusText: response.statusText, errorBody });

      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;
      
      throw new AppError({
        message: `API Error deleting poet ${id}: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, poetId: id }
      });
    }
    
    const deletedPoet: Poet = await response.json();
    console.log(`poetService: deletePoet successful via API for ID: ${id}`);

    if (!deletedPoet || typeof deletedPoet !== 'object' || deletedPoet.id !== id) {
       console.error('poetService: deletePoet received unexpected data format from API', { responseData: deletedPoet, expectedId: id });
       throw new AppError({
          message: `API returned unexpected data format after deleting poet ${id}`,
          type: ErrorType.UNEXPECTED,
          statusCode: 500,
          metadata: { url: apiUrl, responseBody: deletedPoet, poetId: id }
       });
    }

    // Invalidate caches for the deleted poet and related entities
    invalidateRelatedCaches(CacheEntityType.POET, id); 
    
    return deletedPoet; 

  } catch (error) {
    const appError = handleError(error, 'deletePoet', { poetId: id });
    logError(appError, 'deletePoet');
    throw appError;
  }
}

/**
 * Retrieves detailed information about a poet from the API
 * @param poetId - The poet's UUID
 * @returns Detailed poet object (PoetDetails)
 * @throws AppError if the poet is not found or the operation fails
 */
export async function getPoetDetails(poetId: string): Promise<PoetDetails> {
  validate([{ validator: isUUID, value: poetId, message: 'Invalid Poet ID format' }], { poetId });

  const cacheKey = `${CACHE_KEY_PATTERNS.BY_ID(CacheEntityType.POET, poetId)}:details`;
  const cachedDetails = cacheManager.get<PoetDetails>(cacheKey);
  if (cachedDetails) {
    console.log(`poetService: getPoetDetails returning cached data for poet ID ${poetId}`);
    return cachedDetails;
  }
  
  try {
    console.log(`poetService: getPoetDetails fetching from API for poet ID ${poetId}`);
    const apiUrl = `/api/poets/${poetId}/details`; 
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: getPoetDetails API error", { status: response.status, statusText: response.statusText, errorBody, url: apiUrl });

      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;
      
      throw new AppError({
        message: `API Error fetching poet details for ${poetId}: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, poetId: poetId }
      });
    }

    const details: PoetDetails = await response.json();
    console.log(`poetService: getPoetDetails fetched from API successfully for poet ID ${poetId}`);

    // Basic validation: check if poet_id matches
    if (!details || typeof details !== 'object' || details.poet_id !== poetId) {
      console.error('poetService: getPoetDetails received unexpected data format from API', { responseData: details, expectedPoetId: poetId });
      throw new AppError({
        message: `API returned unexpected data format for poet details ${poetId}`,
        type: ErrorType.UNEXPECTED,
        statusCode: 500,
        metadata: { url: apiUrl, responseBody: details, poetId: poetId }
      });
    }
    
    cacheManager.set(cacheKey, details, CACHE_TTL.MEDIUM);
    
    return details;

  } catch (error) {
    const appError = handleError(error, 'getPoetDetails', { poetId });
    logError(appError, 'getPoetDetails');
    throw appError;
  }
}

/**
 * Retrieves all poets with detailed information from the API
 * @returns Array of detailed poet objects (PoetDetails)
 * @throws AppError if the operation fails
 */
export async function getAllPoetDetails(): Promise<PoetDetails[]> {
  const cacheKey = `${CACHE_KEY_PATTERNS.ALL(CacheEntityType.POET)}:details`;
  const cachedDetails = cacheManager.get<PoetDetails[]>(cacheKey);
  if (cachedDetails) {
    console.log("poetService: getAllPoetDetails returning cached data");
    return cachedDetails;
  }
  
  try {
    console.log("poetService: getAllPoetDetails fetching from API");
    const apiUrl = '/api/poets/details';
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: getAllPoetDetails API error", { status: response.status, statusText: response.statusText, errorBody, url: apiUrl });

      const errorType = response.status === 404 ? ErrorType.NOT_FOUND : ErrorType.NETWORK;
      
      throw new AppError({
        message: `API Error fetching all poet details: ${response.statusText} (Status: ${response.status})`,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody }
      });
    }

    const details: PoetDetails[] = await response.json();
    console.log(`poetService: getAllPoetDetails fetched ${details.length} details from API`);
    
    cacheManager.set(cacheKey, details, CACHE_TTL.MEDIUM);
    
    return details;

  } catch (error) {
    const appError = handleError(error, 'getAllPoetDetails');
    logError(appError, 'getAllPoetDetails');
    
    if (appError.type === ErrorType.NOT_FOUND || appError.statusCode === 404) {
      console.log("poetService: getAllPoetDetails returning empty array for NOT_FOUND");
      return [];
    }
    
    throw appError;
  }
}

/**
 * Retrieves a paginated list of poets from the API
 * @param params - Pagination parameters, including optional sortBy
 * @returns Paginated result of poets
 * @throws AppError if the operation fails
 */
export async function getPaginatedPoets(
  params: PaginationParams & { sortBy?: 'name' | 'lastName' } // Add sortBy to params
): Promise<PaginatedResult<Poet>> {
  const { page, pageSize, sortBy = 'name' } = params; // Default sortBy to 'name'
  
  if (page < 1 || pageSize < 1) {
    console.error("poetService: getPaginatedPoets invalid pagination params", params);
    return createPaginatedResult([], 0, { page, pageSize }); // Use corrected params
  }
  
  // Include sortBy in the cache key - Adjusted to avoid linter error
  // Assume PAGINATED generates the base key, append sortBy manually
  const cacheKeyBase = CACHE_KEY_PATTERNS.PAGINATED(CacheEntityType.POET, page, pageSize);
  const cacheKey = `${cacheKeyBase}:sortBy-${sortBy}`;
  
  const cachedResult = cacheManager.get<PaginatedResult<Poet>>(cacheKey);
  if (cachedResult) {
    console.log(`poetService: getPaginatedPoets returning cached data for page ${page}, sortBy ${sortBy}`);
    return cachedResult;
  }

  try {
    console.log(`poetService: getPaginatedPoets fetching from API page: ${page}, size: ${pageSize}, sortBy: ${sortBy}`);
    const query = new URLSearchParams({ 
      page: page.toString(), 
      pageSize: pageSize.toString(),
      sortBy: sortBy // Add sortBy to query params
    });
    const apiUrl = `/api/poets?${query.toString()}`;
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: getPaginatedPoets API error", { status: response.status, statusText: response.statusText, errorBody, url: apiUrl });

      if (response.status === 404) {
        console.log(`poetService: getPaginatedPoets received 404 for page ${page}, returning empty result.`);
        return createPaginatedResult([], 0, { page, pageSize }); // Use corrected params
      }

      throw new AppError({
        message: `API Error fetching paginated poets (page ${page}, sortBy ${sortBy}): ${response.statusText} (Status: ${response.status})`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, page, pageSize, sortBy }
      });
    }

    const result: PaginatedResult<Poet> = await response.json();
    console.log(`poetService: getPaginatedPoets fetched ${result.data.length} poets, total ${result.metadata.totalItems} from API for page ${page}`);

    if (!result || typeof result !== 'object' || !Array.isArray(result.data) || typeof result.metadata?.totalItems !== 'number') {
       console.error('poetService: getPaginatedPoets received unexpected data format from API', { responseData: result });
       throw new AppError({
          message: 'API returned unexpected data format for paginated poets',
          type: ErrorType.UNEXPECTED,
          statusCode: 500,
          metadata: { url: apiUrl, responseBody: result }
       });
    }

    cacheManager.set(cacheKey, result, CACHE_TTL.MEDIUM);
    return result;

  } catch (error) {
    console.error("poetService: getPaginatedPoets error", error);
    const appError = handleError(error, 'getPaginatedPoets', { page, pageSize, sortBy });
    logError(appError, 'getPaginatedPoets');

    if (appError.type === ErrorType.NOT_FOUND || appError.statusCode === 404) {
      console.log(`poetService: getPaginatedPoets returning empty result (error handling) for page ${page}`);
      return createPaginatedResult([], 0, { page, pageSize }); // Use corrected params
    }

    throw appError;
  }
}

/**
 * Retrieves poets by era from the API
 * @param era - The era to filter poets by.
 * @returns Array of poet objects from that era.
 * @throws AppError if the operation fails
 */
export async function getPoetsByEra(era: string): Promise<Poet[]> {
  console.log(`poetService: getPoetsByEra called for era: ${era}`);

  if (!era || typeof era !== 'string') {
    console.warn("poetService: getPoetsByEra called with empty or invalid era.");
    return [];
  }

  const cacheKey = `${CacheEntityType.POET}:era:${era.toLowerCase()}`;
  const cachedPoets = cacheManager.get<Poet[]>(cacheKey);
  if (cachedPoets) {
    console.log(`poetService: getPoetsByEra returning cached results for era: ${era}`);
    return cachedPoets;
  }

  try {
    console.log(`poetService: getPoetsByEra fetching from API for era: ${era}`);
    const query = new URLSearchParams({ era: era });
    const apiUrl = `/api/poets?${query.toString()}`;
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: getPoetsByEra API error", { status: response.status, statusText: response.statusText, errorBody, url: apiUrl });

      if (response.status === 404) {
        console.log(`poetService: getPoetsByEra received 404 for era: ${era}, returning empty array.`);
        return [];
      }

      throw new AppError({
        message: `API Error fetching poets for era "${era}": ${response.statusText} (Status: ${response.status})`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, era }
      });
    }

    const poets: Poet[] = await response.json();
    console.log(`poetService: getPoetsByEra fetched ${poets.length} results from API for era: ${era}`);
    cacheManager.set(cacheKey, poets, CACHE_TTL.MEDIUM);
    return poets;

  } catch (error) {
    console.error("poetService: getPoetsByEra error", error);
    const appError = handleError(error, 'getPoetsByEra', { era });
    logError(appError, 'getPoetsByEra');

    if (appError.type === ErrorType.NOT_FOUND || appError.statusCode === 404) {
      console.log(`poetService: getPoetsByEra returning empty array (error handling) for era: ${era}`);
      return [];
    }

    throw appError;
  }
}

/**
 * Fetches a list of distinct eras from the API
 * @returns Array of distinct era strings.
 * @throws AppError if the operation fails
 */
export async function getDistinctEras(): Promise<string[]> {
  console.log("poetService: getDistinctEras called");

  const cacheKey = `${CacheEntityType.POET}:eras:distinct`;
  const cachedEras = cacheManager.get<string[]>(cacheKey);
  if (cachedEras) {
    console.log("poetService: getDistinctEras returning cached results");
    return cachedEras;
  }

  try {
    console.log("poetService: getDistinctEras fetching from API");
    const apiUrl = `/api/poets/eras`;
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: getDistinctEras API error", { status: response.status, statusText: response.statusText, errorBody, url: apiUrl });

      if (response.status === 404) {
        console.log("poetService: getDistinctEras received 404, returning empty array.");
        return [];
      }

      throw new AppError({
        message: `API Error fetching distinct eras: ${response.statusText} (Status: ${response.status})`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody }
      });
    }

    const eras: string[] = await response.json();
    console.log(`poetService: getDistinctEras fetched ${eras.length} distinct eras from API`);

    cacheManager.set(cacheKey, eras, CACHE_TTL.LONG); 
    return eras;

  } catch (error) {
    console.error("poetService: getDistinctEras error", error);
    const appError = handleError(error, 'getDistinctEras');
    logError(appError, 'getDistinctEras');

    if (appError.type === ErrorType.NOT_FOUND || appError.statusCode === 404) {
      console.log("poetService: getDistinctEras returning empty array (error handling).");
      return [];
    }

    throw appError;
  }
}

/**
 * Fetches a list of distinct initial letters of poet last names from the API
 * @returns Array of distinct, sorted uppercase letters.
 * @throws AppError if the operation fails
 */
export async function getDistinctInitialLetters(): Promise<string[]> {
  const cacheKey = `${CACHE_KEY_PATTERNS.ALL(CacheEntityType.POET)}:initial-letters`;
  const cachedLetters = cacheManager.get<string[]>(cacheKey);
  if (cachedLetters) {
    console.log("poetService: getDistinctInitialLetters returning cached results");
    return cachedLetters;
  }

  // Use executeServiceOperation and properly handle the ServiceResult
  const serviceResult: ServiceResult<string[]> = await executeServiceOperation(async () => {
      console.log("poetService: getDistinctInitialLetters fetching from API");
      const apiUrl = '/api/poets/initial-letters';
      const response = await fetchWithAuth(apiUrl);

      if (!response.ok) {
          let errorBody: any = null;
          try { errorBody = await response.json(); } catch (e) { /* ignore */ }
          console.error("poetService: getDistinctInitialLetters API error", { status: response.status, statusText: response.statusText, errorBody, url: apiUrl });
          
          throw new AppError({
              message: `API Error fetching distinct initial letters: ${response.statusText} (Status: ${response.status})`,
              type: ErrorType.NETWORK,
              statusCode: response.status,
              metadata: { url: apiUrl, responseBody: errorBody }
          });
      }

      const letters: string[] = await response.json();
      console.log(`poetService: getDistinctInitialLetters fetched ${letters.length} letters`);
      
      // Basic validation
      if (!Array.isArray(letters) || letters.some(l => typeof l !== 'string' || l.length !== 1)) {
          console.error("poetService: getDistinctInitialLetters received unexpected data format from API", { letters });
          throw new AppError({
              message: 'API returned unexpected data format for initial letters',
              type: ErrorType.UNEXPECTED,
              statusCode: 500,
              metadata: { url: apiUrl, responseBody: letters }
          });
      }
      
      // Cache the result before returning it from the operation function
      cacheManager.set(cacheKey, letters, 1800000); // Corrected: 3 args, use literal for TTL (30*60*1000)
      return letters;

  }, 'getDistinctInitialLetters');
  
  // Check the result from executeServiceOperation
  if (serviceResult.error) {
      // Throw the error if the operation failed
      throw serviceResult.error;
  } 
  
  // Check if data is null (shouldn't happen if no error, but good practice)
  if (serviceResult.data === null) {
      console.error("getDistinctInitialLetters: Service operation succeeded but returned null data");
      throw new AppError({
          message: "Failed to retrieve distinct initial letters, operation returned null data.",
          type: ErrorType.UNEXPECTED
      });
  }
  
  // Return the data array if successful
  return serviceResult.data;
}

/**
 * Searches all poets by name via the API
 * @param term - The search term to look for in poet names
 * @param sortBy - Optional sort order ('name' or 'lastName').
 * @returns Array of matching poets.
 * @throws AppError if the operation fails.
 */
export async function searchAllPoets(term: string, sortBy?: 'name' | 'lastName'): Promise<Poet[]> {
  const searchTerm = term.trim();
  if (!searchTerm) {
    console.log("poetService: searchAllPoets called with empty term, returning empty array.");
    return [];
  }

  // Note: Search results are generally not cached, as terms are highly variable.

  try {
    console.log(`poetService: searchAllPoets fetching from API term: "${searchTerm}", sortBy: ${sortBy || 'default'}`);
    const query = new URLSearchParams({ term: searchTerm });
    if (sortBy) {
      query.set('sortBy', sortBy);
    }
    const apiUrl = `/api/poets/search?${query.toString()}`;
    const response = await fetchWithAuth(apiUrl);

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: searchAllPoets API error", { status: response.status, statusText: response.statusText, errorBody, url: apiUrl });

      throw new AppError({
        message: `API Error searching poets for term "${searchTerm}": ${response.statusText} (Status: ${response.status})`,
        type: ErrorType.NETWORK,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, searchTerm, sortBy }
      });
    }

    const results: Poet[] = await response.json();
    // console.log(`poetService: searchAllPoets received ${results.length} results from API for term "${searchTerm}"`, results); // <-- Removed debug log

    return results;

  } catch (error) {
    console.error("poetService: searchAllPoets error", error);
    const appError = handleError(error, 'searchAllPoets', { searchTerm, sortBy });
    logError(appError, 'searchAllPoets');
    throw appError;
  }
}

/**
 * Fetches the target page number for a given letter based on backend calculation.
 * @param letter - The single uppercase letter to find.
 * @param pageSize - The page size used for pagination.
 * @param sortBy - Must be 'lastName' for this function.
 * @returns Object containing the target page number and the ID of the first poet on that page.
 * @throws AppError if validation fails or the operation fails
 */
export async function getPageForLetter(
  letter: string,
  pageSize: number,
  sortBy: 'lastName' // Currently restricted to lastName
): Promise<{ targetPage: number; firstPoetId: string | null }> { // <-- Updated return type

  // Basic Input Validation
  if (!letter || letter.length !== 1 || !/^[A-Z]$/.test(letter)) {
    throw new AppError({
      message: 'Invalid letter parameter. Must be a single uppercase letter.',
      type: ErrorType.VALIDATION,
      metadata: { letter }
    });
  }
  if (typeof pageSize !== 'number' || !Number.isInteger(pageSize) || pageSize <= 0) {
    throw new AppError({
      message: 'Invalid pageSize parameter. Must be a positive integer.',
      type: ErrorType.VALIDATION,
      metadata: { pageSize }
    });
  }
  if (sortBy !== 'lastName') {
    throw new AppError({
      message: 'Invalid sortBy parameter. Currently must be "lastName".',
      type: ErrorType.VALIDATION,
      metadata: { sortBy }
    });
  }

  // Cache Key
  const cacheKey = `${CACHE_KEY_PATTERNS.ALL(CacheEntityType.POET)}:page-for-letter:${letter}:${pageSize}:${sortBy}`;
  
  // Type for the expected API response
  type PageForLetterResponse = { 
    targetPage: number; 
    firstPoetId: string | null; 
  }; // <-- Define expected response structure

  // Use getWithCache for concise caching logic
  return getWithCache<PageForLetterResponse>(
    cacheKey,
    async () => {
      console.log(`poetService: getPageForLetter fetching from API for letter ${letter}, size ${pageSize}`);
      const query = new URLSearchParams({
        letter: letter,
        pageSize: pageSize.toString(),
        sortBy: sortBy
      });
      const apiUrl = `/api/poets/page-for-letter?${query.toString()}`;
      const response = await fetchWithAuth(apiUrl);

      if (!response.ok) {
        let errorBody: any = null;
        try { errorBody = await response.json(); } catch (e) { /* ignore */ }
        console.error("poetService: getPageForLetter API error", { status: response.status, statusText: response.statusText, errorBody, url: apiUrl });

        throw new AppError({
          message: `API Error getting page for letter "${letter}": ${response.statusText} (Status: ${response.status})`,
          type: ErrorType.NETWORK,
          statusCode: response.status,
          metadata: { url: apiUrl, responseBody: errorBody, letter, pageSize, sortBy }
        });
      }

      const result = await response.json() as PageForLetterResponse; // <-- Assert type
      console.log(`poetService: getPageForLetter received page ${result.targetPage}, ID ${result.firstPoetId} from API`);

      // Validate the structure
      if (typeof result?.targetPage !== 'number' || !('firstPoetId' in result)) {
          console.error("poetService: getPageForLetter received unexpected data format", { responseData: result });
          throw new AppError({
              message: "API returned unexpected data format for page-for-letter",
              type: ErrorType.UNEXPECTED,
              metadata: { url: apiUrl, responseBody: result }
          });
      }
      
      return result; // Return the validated result
    },
    CACHE_TTL.MEDIUM // Use appropriate TTL
  );
}

/**
 * Generates 4 style points for a given poet using an AI service.
 * @param poetName - The name of the poet.
 * @param poetryUrl - The Poetry Foundation URL for the poet (optional).
 * @returns A promise that resolves to an array of 4 style point strings.
 * @throws AppError if the AI service call fails, returns invalid data, or doesn't return 4 points.
 */
export async function generatePoetStylePoints(poetName: string, poetryUrl?: string | null): Promise<StylePoints> {
  console.log(`poetService: generatePoetStylePoints called for: ${poetName}`);

  // Basic validation
  if (!poetName || typeof poetName !== 'string') {
    throw new AppError('Invalid poet name provided for style point generation.', ErrorType.VALIDATION);
  }
  // Optional: Validate URL format if provided
  if (poetryUrl) {
    try {
      new URL(poetryUrl);
    } catch (_) {
      throw new AppError('Invalid Poetry Foundation URL provided for style point generation.', ErrorType.VALIDATION);
    }
  }
  
  // Define the base URL *inside* the function, only when called by backend
  const INTERNAL_API_BASE_URL = process.env.INTERNAL_API_BASE_URL || 'http://localhost:3001';
  const endpointPath = '/api/ai/generate-style-points';
  const apiUrl = `${INTERNAL_API_BASE_URL}${endpointPath}`;
  
  try {
    console.log(`poetService: Calling POST ${apiUrl} for poet: ${poetName}`);
    const response = await fetchWithAuth(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ poetName, poetryUrl }),
    });

    if (!response.ok) {
      let errorBody: any = null;
      try { errorBody = await response.json(); } catch (e) { /* ignore */ }
      console.error("poetService: generatePoetStylePoints API error", { status: response.status, statusText: response.statusText, errorBody, poetName });
      
      let message = `AI service failed to generate style points for ${poetName}: ${response.statusText} (Status: ${response.status})`;
      let errorType: ErrorType = ErrorType.AI_SERVICE_ERROR;
      if (response.status === 429) {
         message = `AI service rate limit exceeded while generating style points for ${poetName}. Please try again later.`;
         errorType = ErrorType.RATE_LIMIT_ERROR; 
      } else if (response.status >= 500) {
         message = `AI service encountered an internal error generating style points for ${poetName}.`;
         errorType = ErrorType.AI_SERVICE_UNAVAILABLE; 
      } else if (response.status === 401 || response.status === 403) {
         message = `AI service authentication failed calling internal endpoint for ${poetName}.`;
         errorType = ErrorType.AUTHENTICATION_ERROR; 
      }
      
      throw new AppError({
        message,
        type: errorType,
        statusCode: response.status,
        metadata: { url: apiUrl, responseBody: errorBody, poetName, poetryUrl }
      });
    }

    const result: StylePointResponse = await response.json();
    console.log(`poetService: generatePoetStylePoints received response for ${poetName}`);

    // Validate the response structure and content
    if (!result || !Array.isArray(result.style_points) || result.style_points.length !== 4 || !result.style_points.every(p => typeof p === 'string' && p.trim() !== '')) {
      console.error('poetService: generatePoetStylePoints received invalid data format from AI service', { responseData: result, poetName });
      throw new AppError({
        message: `AI service returned invalid data format for style points for ${poetName}. Expected 4 points.`, 
        type: ErrorType.UNEXPECTED,
        statusCode: 500, 
        metadata: { url: apiUrl, responseBody: result, poetName }
      });
    }
    
    console.log(`poetService: generatePoetStylePoints successful for ${poetName}`);
    return result.style_points as StylePoints; // Cast to StylePoints for type safety

  } catch (error) {
    // Handle network errors or errors thrown during response processing
    const appError = handleError(error, 'generatePoetStylePoints', { poetName, poetryUrl });
    logError(appError, 'generatePoetStylePoints');
    // Re-throw the standardized AppError
    throw appError;
  }
} 